// @ts-strict-ignore
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Accordion, Radio, ShowMoreButton, Spacer } from "@aviary";
import { useHealthConditionsQuery } from "@shared/data/queries/HealthConditionsQuery/HealthConditions.query";
import { FilterType } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { l } from "@shared/locales/i18n";

import { FilterTitle } from "../shared/FilterTitle/FilterTitle";

import * as styles from "./HealthConditions.styles";

interface Props {
  isBold?: boolean;
  logFilterEvent: (entityName: string, entityType: string) => void;
}

const HealthConditions = ({ isBold, logFilterEvent }: Props) => {
  const { t } = useTranslation();
  const { activeConditions, setActiveConditions } = useCatalogSearch();
  const { data, loading } = useHealthConditionsQuery();
  const [activeCondition] = activeConditions ?? [];

  const showLessCutoff = 5;
  const conditions = data?.viewer?.healthConditions ?? [];

  const activeConditionIndex = conditions.findIndex(
    condition => condition?.id === activeCondition?.id
  );

  const [showAll, setShowAll] = useState(activeConditionIndex >= showLessCutoff);
  const expandAccordionOnInit = activeConditionIndex >= 0;

  if (loading || !data?.viewer?.healthConditions) return null;

  const onConditionClick = ({ id: clickedId, name }) => {
    const distanceFromTop = window?.scrollY;
    const isClickedConditionActive = activeCondition?.id === clickedId;
    if (isClickedConditionActive) {
      setActiveConditions([]);
    } else {
      logFilterEvent(name, FilterType.CONDITION);
      setActiveConditions([
        {
          id: clickedId,
          name,
        },
      ]);
    }
    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  const isChecked = ({ id: radioConditionId }) => activeCondition?.id === radioConditionId;

  const renderConditions = () =>
    conditions.map((condition, index) => {
      if (showAll || index < showLessCutoff) {
        return (
          <div key={condition.id} css={styles.spacer}>
            <Radio
              id={condition.id}
              checked={isChecked(condition)}
              onChange={() => onConditionClick(condition)}
            >
              {condition.name}
            </Radio>
          </div>
        );
      }

      return null;
    });

  return (
    <Accordion
      coreTriggerContent={
        <FilterTitle
          title={t(l.sharedCatalog.filter.HealthConditions)}
          count={activeConditions?.length}
          isSecondaryWeight={isBold}
          isBadge
        />
      }
      isDefaultExpanded={expandAccordionOnInit}
    >
      <div css={styles.checkboxList}>
        {renderConditions()}
        <Spacer height="half" />
        <ShowMoreButton onClick={() => setShowAll(!showAll)} isExpanded={showAll} />
      </div>
    </Accordion>
  );
};

export { HealthConditions };
